import { useMemo } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';

import { CHANNEL_SETTINGS } from '../../../components/TopSignatureSources/constants';

type TopSignatureSourcesConfig = StarterDashboardV2PageData['featureConfigs']['topSignatureSourcesConfig'];

export function useSignatureSources() {
	const {
		data: {
			petition,
			featureConfigs: { topSignatureSourcesConfig },
		},
	} = useStarterDashboardV2PageData();

	const petitionPublishedAt = petition.publishedAt;

	const shouldShowSignatureSources = useMemo(() => {
		const config = topSignatureSourcesConfig;
		if (!config.enabled) return false;

		if (!petitionPublishedAfterEarliestDate(petitionPublishedAt, config)) return false;

		const signatureCounts = petition.signatureCountsByChannel;
		if (signatureCounts.__typename !== 'SignatureCountsByChannel') return false;

		// Checks the channel settings for each channel in list
		const missingTranslations = signatureCounts.countsByChannel.filter(
			({ channel }) => !Object.hasOwn(CHANNEL_SETTINGS, channel),
		);

		const hasAllMissingTranslations = missingTranslations.length === signatureCounts.countsByChannel.length;
		if (hasAllMissingTranslations) return false;

		const hasSignatureCounts = signatureCounts.totalCount > 0;
		if (!hasSignatureCounts) return false;

		const hasOtherCategory = signatureCounts.countsByChannel.filter(({ channel }) => channel === 'other').length === 1;

		// Includes edge case of missing translations that are consolidated into 'other' channel
		const hasOnlyOtherSource =
			(signatureCounts.countsByChannel.length === 1 && hasOtherCategory) ||
			(hasOtherCategory && missingTranslations.length === signatureCounts.countsByChannel.length - 1);
		if (hasOnlyOtherSource) return false;

		const hasMinimumSignatureCount = signatureCounts.totalCount >= config.minimum_signature_count;
		if (!hasMinimumSignatureCount) return false;

		return true;
	}, [petitionPublishedAt, topSignatureSourcesConfig, petition.signatureCountsByChannel]);

	return {
		shouldShowSignatureSources,
	};
}

function petitionPublishedAfterEarliestDate(publishedAt: string | null, config: TopSignatureSourcesConfig) {
	if (!publishedAt) return false;
	return new Date(publishedAt) >= new Date(config.earliest_petition_published_at);
}
